import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/response/TireSearchResponse';
import { TFunction } from 'i18next';
import {
  FILTER_BRAND,
  FILTER_DELIVERY,
  getStockFilterOptions,
  RangeFacetLocal,
  TextFacetLocal,
  TextFilterItem,
} from 'components/Filter/Filter.types';
import { getStocksMapV2 } from 'domains/references';
import { TireBrandDetailLocal } from 'domains/tires/Tire.types';
import { SearchData } from 'utils';
import { getTireDeliveryFilterOptions, getUpdatedTireRangeFilters, getUpdatedTireTextFilters } from './TireFilterUtils';

export const getFilteredInitialTextFilters = (
  initialTextFilters: TextFilterItem[] | undefined,
  availableTires: TireItem[],
): TextFilterItem[] => {
  return (
    initialTextFilters
      ?.map(({ id, item }) => {
        if (id !== FILTER_BRAND) {
          return { id, item };
        }

        return availableTires.some((tire) => tire.brandId === item) ? { id, item } : undefined;
      })
      .filter((filterItem): filterItem is TextFilterItem => filterItem !== undefined) ?? []
  );
};

const getNextTextFilters = ({
  catalogTextFilters,
  stocks,
  availableTires,
  brandCategoriesMap,
  t,
}: {
  catalogTextFilters: TextFacetLocal[];
  stocks: ReturnType<typeof getStocksMapV2>;
  availableTires: TireItem[];
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>;
  t: TFunction;
}): TextFacetLocal[] => {
  const updatedTextFilters = getUpdatedTireTextFilters(
    catalogTextFilters,
    stocks,
    availableTires,
    brandCategoriesMap,
    t,
  );

  if (stocks.size === 0) {
    return updatedTextFilters;
  }

  const stocksFilterOptions = getStockFilterOptions('TIRES', true);
  const deliveryFilterOptions = getTireDeliveryFilterOptions(stocksFilterOptions, stocks, availableTires, t);
  const hasDeliveryFilter = updatedTextFilters.some((filter) => filter.id === FILTER_DELIVERY);

  if (!hasDeliveryFilter) {
    return updatedTextFilters.concat(deliveryFilterOptions);
  }

  return updatedTextFilters.map((filter) => (filter.id === FILTER_DELIVERY ? deliveryFilterOptions : filter));
};

const getMissingSelectedDeliveryOptions = (
  deliveryFilterSelection: string[] | undefined,
  deliveryFilterOptions: TextFacetLocal | undefined,
): string[] => {
  if (!deliveryFilterSelection || !deliveryFilterOptions) {
    return [];
  }

  return deliveryFilterSelection.filter((selection) => {
    const updatedDelivery = deliveryFilterOptions.items.find((item) => item.label === selection);
    return updatedDelivery === undefined || updatedDelivery.numberOfItems === 0;
  });
};

export const getNextFilterSectionState = ({
  initialTireTextFilters,
  stocks,
  availableTires,
  brandCategoriesMap,
  t,
  deliverySelection,
  rangeFilters,
  filteredTires,
}: {
  initialTireTextFilters: TextFacetLocal[];
  stocks: ReturnType<typeof getStocksMapV2>;
  availableTires: TireItem[];
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>;
  t: TFunction;
  deliverySelection: string[] | undefined;
  rangeFilters: RangeFacetLocal[];
  filteredTires: TireItem[];
}): {
  nextTextFilters: TextFacetLocal[];
  shouldResetDeliveryFilter: boolean;
  updatedRangeFilters: RangeFacetLocal[];
} => {
  const nextTextFilters = getNextTextFilters({
    catalogTextFilters: initialTireTextFilters,
    stocks,
    availableTires,
    brandCategoriesMap,
    t,
  });
  const deliveryFilterOptions = nextTextFilters.find((filter) => filter.id === FILTER_DELIVERY);
  const missingSelectedDeliveryOptions = getMissingSelectedDeliveryOptions(deliverySelection, deliveryFilterOptions);

  return {
    nextTextFilters,
    shouldResetDeliveryFilter: missingSelectedDeliveryOptions.length === 1,
    updatedRangeFilters: getUpdatedTireRangeFilters(rangeFilters, filteredTires),
  };
};

export const shouldInitializeTextFilters = (
  someStockMissingFirstLoad: boolean,
  someStockLoading: boolean,
  initialTextFilters: TextFilterItem[] | undefined,
): initialTextFilters is TextFilterItem[] => !someStockMissingFirstLoad && !someStockLoading && !!initialTextFilters;
